import { Switch, Route, Redirect, Router } from "wouter";
import { UnsavedChangesProvider } from "@/context/UnsavedChangesProvider";
import { BrowserHistoryGuard } from "@/components/BrowserHistoryGuard";
import { RouteAnnouncer } from "@/components/RouteAnnouncer";
import {
  FINANCES_PATHS,
  FINANCES_INVOICES_EDIT_ROUTE,
  FINANCES_TRIP_EDIT_ROUTE,
} from "@/lib/financesPaths";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/Dashboard";
import Homepage from "@/pages/Homepage";
import Trips from "@/pages/Trips";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import TripDetail from "@/pages/TripDetail";
import { ClientList } from "@/pages/ClientList";
import { ClientDetail } from "@/pages/ClientDetail";
import { ClientForm } from "@/pages/ClientForm";
import { ProposalView } from "@/pages/ProposalView";
import { SalesPlanPage } from "@/pages/SalesPlanPage";
import WaitingList from "@/pages/WaitingList";
import { AuthProvider } from "./context/AuthContext";
import { ImpersonationProvider, useUnclaimedAccess } from "./context/ImpersonationContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import MainLayout from "@/components/MainLayout";
import AdminShell from "@/components/AdminShell";
import PlaceholderPage from "@/pages/PlaceholderPage";
import DeferredFeaturePlaceholder from "@/components/DeferredFeaturePlaceholder";
import { isFeatureEnabled } from "@/config/featureFlags";
import { useResolvedFeatureFlags } from "@/hooks/useResolvedFeatureFlags";
import type { FeatureFlagKey } from "@/config/featureFlags";
import { AlphaGateGuard } from "@/components/AlphaGateGuard";
import { Settings, User, Plane, CreditCard, BarChart3, Globe } from "lucide-react";
import { Toaster as UseToastToaster } from '@/components/ui/use-toast';
import UserProfile from "@/components/UserProfile";
import Auth0ConfigBanner from "@/components/Auth0ConfigBanner";

// New pages
import AIAssistant from "@/pages/AIAssistant";
import SmartAssistant from "@/pages/SmartAssistant";
import CanvasUI from "@/pages/CanvasUI";
import PromptManager from "@/pages/PromptManager";
import SiteSettings from "@/pages/SiteSettings";
import BookingTools from "@/pages/BookingTools";
import Invoicing from "@/pages/Invoicing";
import Statements from "@/pages/Statements";
import Reports from "@/pages/Reports";
import CreateInvoice from "@/pages/CreateInvoice";
import EditInvoice from "@/pages/EditInvoice";
import EditTrip from "@/pages/EditTrip";
import PaymentAuthorizations from "@/pages/PaymentAuthorizations";
import Reporting from "@/pages/Reporting";
import WebsiteManager from "@/pages/WebsiteManager";
import Marketing from "@/pages/Marketing";
import Knowledge from "@/pages/Knowledge";
import Training from "@/pages/Training";
import SalesPipeline from "@/pages/SalesPipeline";
import MCPToolManagement from "@/pages/admin/MCPToolManagement";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminUserRecordPage from "@/pages/admin/AdminUserRecordPage";
import SkillsManagementPage from "@/pages/admin/SkillsManagementPage";
import MemberActivationModulesPage from "@/pages/admin/MemberActivationModulesPage";
import MemberActivationArchivePage from "@/pages/admin/MemberActivationArchivePage";
import MemberActivationKpisPage from "@/pages/admin/MemberActivationKpisPage";
import OrchestrationRolloutPage from "@/pages/admin/OrchestrationRolloutPage";
import AdminAuditPage from "@/pages/admin/AdminAuditPage";
import AdminPromptsPage from "@/pages/admin/AdminPromptsPage";
import AdminContentPage from "@/pages/admin/AdminContentPage";
import AdminFeatureGatesPage from "@/pages/admin/AdminFeatureGatesPage";
import CostDashboardPage from "@/pages/admin/CostDashboardPage";
import ChatExportPage from "@/pages/admin/ChatExportPage";
import AdminProductUpdatesPage from "@/pages/admin/AdminProductUpdatesPage";
import SyncErrorLogPage from "@/pages/admin/SyncErrorLogPage";
import AccessDenied from "@/pages/AccessDenied";
import ProductUpdates from "@/pages/ProductUpdates";
import ProductUpdatePopup from "@/components/ProductUpdatePopup";
import { ADMIN_ROLE_SLUGS } from "@/lib/roles";
import TeamManagement from "@/pages/TeamManagement";
import UserPreferences from "@/pages/UserPreferences";
import SettingsPage from "@/pages/SettingsPage";
import Workspace from "@/pages/Workspace";
import Phase1Layout from "@/components/Phase1Layout";
import { PHASE1_PAGE_COMPONENTS } from "@/pages/phase1";
import {
  PHASE1_CRM_REDIRECTS,
  PHASE1_FLAGS,
  PHASE1_LEGACY_REDIRECTS,
  PHASE1_PAGES,
  PHASE1_ROUTE_ORDER,
} from "@/lib/phase1Paths";
import KnowledgeGraphDemo from "@/pages/KnowledgeGraphDemo";
import CanvasDashboard from "@/pages/CanvasDashboard";
import CanvasClients from "@/pages/CanvasClients";
import CanvasTrips from "@/pages/CanvasTrips";
import Home from "@/pages/Home";
// import TripsCanvasPage from "@/pages/TripsCanvasPage"; // removed duplicate
import ErrorBoundary from "@/components/ErrorBoundary";
import TripPlannerCanvas from "@/pages/TripPlannerCanvas";
import UnclaimedCommissions from "@/pages/UnclaimedCommissions";
import Checkout from "@/pages/Checkout";
import CheckoutErrorFallback from "@/components/checkout/CheckoutErrorFallback";
import FinancesLayout from "@/components/FinancesLayout";
import AccountLayout from "@/components/AccountLayout";
import {
  ACCOUNT_CENTER_FLAGS,
  ACCOUNT_HOME,
  ACCOUNT_HOME_PAGE,
  ACCOUNT_PATHS,
  ACCOUNT_PAGE_IDS,
  type AccountPageId,
} from "@/lib/accountPaths";
import {
  AccountPageGuard,
  AccountPersonal,
  AccountPassword,
  AccountGem,
  AccountBusiness,
  AccountPublic,
  AccountTeam,
  AccountMembership,
  AccountBilling,
  AccountDocuments,
} from "@/pages/account";
import SettingsLayout from "@/components/SettingsLayout";
import {
  SETTINGS_ALL_PAGE_IDS,
  SETTINGS_HOME_PAGE,
  SETTINGS_MODULE_FLAGS,
  SETTINGS_PATHS,
  SETTINGS_RETIRED_PATHS,
  type SettingsPageId,
} from "@/lib/settingsPaths";
import {
  SettingsColorTheme,
  SettingsAivia,
  SettingsCrm,
  SettingsWebsite,
  SettingsProposals,
  SettingsPayments,
} from "@/pages/settings";

function UnclaimedAccessGate({ children }: { children: React.ReactNode }) {
  const hasUnclaimedAccess = useUnclaimedAccess();
  if (hasUnclaimedAccess === undefined) return null;
  if (!hasUnclaimedAccess) return <NotFound />;
  return <>{children}</>;
}

function FeatureFlagGate({
  flag,
  children,
  deniedFallback,
}: {
  /**
   * One flag, or a set of which EVERY one must be on. A set is how a sub-flag
   * is gated by its parent: pass [parent, sub], parent first.
   */
  flag: FeatureFlagKey | readonly FeatureFlagKey[];
  children: React.ReactNode;
  /** Rendered when the flag is disabled for this user. Defaults to <NotFound />. */
  deniedFallback?: React.ReactNode;
}) {
  const { isEnabled, isLoading, error, refetch } = useResolvedFeatureFlags();
  if (isLoading) return null;
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-sm text-muted-foreground">
        <p>Unable to load feature configuration.</p>
        <button
          onClick={() => refetch()}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }
  const required = Array.isArray(flag) ? flag : [flag as FeatureFlagKey];
  if (!required.every((f) => isEnabled(f))) {
    return <>{deniedFallback ?? <NotFound />}</>;
  }
  return <>{children}</>;
}

/**
 * AR-380 — the Account center REPLACES the Profile and Team Management front
 * end. While the migration flag is on, their addresses land on the page that
 * now holds them, so a bookmark and a stale link both still work. While it is
 * off, the old page renders unchanged.
 */
function ReplacedByAccountCenter({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  const { isEnabled, isLoading } = useResolvedFeatureFlags();
  if (isLoading) return null;
  // An unresolved flag deliberately falls through to the legacy page rather
  // than blanking it: the flag is off in every environment by default, so the
  // legacy surface IS the correct answer when we cannot ask. React Query keeps
  // the last resolved value across a failed refetch, so only a first load that
  // never succeeds reaches this, and a blank Profile would be the worse failure.
  //
  // It reads the SAME flag set the routes do. Redirecting on the parent alone
  // would send the member to an Account center the sub-flag has switched off.
  if (ACCOUNT_CENTER_FLAGS.every((f) => isEnabled(f))) return <Redirect to={to} />;
  return <>{children}</>;
}

/**
 * AR-390 — `/settings` is the Settings module's home while the migration flags
 * are on, and the legacy settings page while they are off. Same shape as
 * ReplacedByAccountCenter, and same reason for the fall-through: the flags are
 * off in every environment by default, so the legacy surface IS the correct
 * answer when we cannot ask.
 */
function ReplacedBySettingsModule({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  const { isEnabled, isLoading } = useResolvedFeatureFlags();
  if (isLoading) return null;
  if (SETTINGS_MODULE_FLAGS.every((f) => isEnabled(f))) return <Redirect to={to} />;
  return <>{children}</>;
}

/** AR-390 — one element per Settings page, keyed by the id the router walks. */
const SETTINGS_PAGE_COMPONENTS: Record<SettingsPageId, React.ReactElement> = {
  theme: <SettingsColorTheme />,
  aivia: <SettingsAivia />,
  crm: <SettingsCrm />,
  website: <SettingsWebsite />,
  proposals: <SettingsProposals />,
  payments: <SettingsPayments />,
};

/** AR-380 — one element per Account page, keyed by the id the router walks. */
const ACCOUNT_PAGE_COMPONENTS: Record<AccountPageId, React.ReactElement> = {
  personal: <AccountPersonal />,
  gem: <AccountGem />,
  business: <AccountBusiness />,
  membership: <AccountMembership />,
  billing: <AccountBilling />,
  public: <AccountPublic />,
  documents: <AccountDocuments />,
  team: <AccountTeam />,
  password: <AccountPassword />,
};

/**
 * AR-445 — the Phase-1 screens and the old addresses that now land on them.
 *
 * Returned only while the Phase-1 flags are on (or still resolving), so with
 * them off the Switch never sees these routes: `/marketing`, `/booking-tools`,
 * `/pipeline`, `/clients` and `/trips` keep today's pages exactly, and every
 * new address falls through to the 404. While the flags resolve, the routes
 * render nothing rather than flashing a 404 or the legacy page.
 */
function phase1Routes(state: "on" | "loading") {
  const pages = PHASE1_ROUTE_ORDER.map((id) => {
    const Page = PHASE1_PAGE_COMPONENTS[id];
    return (
      <Route key={id} path={PHASE1_PAGES[id].path}>
        {() =>
          state === "loading" ? null : (
            <ProtectedRoute>
              <FeatureFlagGate flag={PHASE1_FLAGS}>
                <Phase1Layout>
                  <ErrorBoundary>
                    <Page />
                  </ErrorBoundary>
                </Phase1Layout>
              </FeatureFlagGate>
            </ProtectedRoute>
          )
        }
      </Route>
    );
  });
  const redirects = Object.entries({ ...PHASE1_CRM_REDIRECTS, ...PHASE1_LEGACY_REDIRECTS }).map(
    ([from, to]) => (
      <Route key={from} path={from}>
        {() => (state === "loading" ? null : <Redirect to={PHASE1_PAGES[to].path} />)}
      </Route>
    ),
  );
  return [...redirects, ...pages];
}

function AppRoutes() {
  const { isEnabled: isFlagEnabled, isLoading: flagsLoading } = useResolvedFeatureFlags();
  const phase1State = flagsLoading
    ? "loading"
    : PHASE1_FLAGS.every((f) => isFlagEnabled(f))
      ? "on"
      : "off";
  return (
    <Switch>
      {phase1State !== "off" ? phase1Routes(phase1State) : null}
      {/* Public member checkout - WorldVia-branded chrome, not MainLayout.
          Gated on the `onboarding` flag (AR-171), which is off by default.
          Uses the build/runtime flag rather than <FeatureFlagGate>: the resolved
          flags endpoint returns false for anonymous visitors, and this route's
          whole audience is signed out. Uses a detail-free error fallback for the
          same reason. */}
      <Route path="/checkout">
        {() =>
          isFeatureEnabled('onboarding') ? (
            <ErrorBoundary fallback={<CheckoutErrorFallback />}>
              <Checkout />
            </ErrorBoundary>
          ) : (
            <NotFound />
          )
        }
      </Route>

      {/* Threads page (renamed from /ai-smart) */}
      <Route path="/threads">
        {() => (
          <ProtectedRoute>
            <ErrorBoundary>
              <CanvasUI />
            </ErrorBoundary>
          </ProtectedRoute>
        )}
      </Route>

      {/* Trips canvases - use CanvasUI shell, not MainLayout */}
      <Route path="/trips">
        {() => (
          <ProtectedRoute>
            <AlphaGateGuard feature="trips">
              <ErrorBoundary>
                <CanvasUI />
              </ErrorBoundary>
            </AlphaGateGuard>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/trips/new">
        {() => (
          <ProtectedRoute>
            <AlphaGateGuard feature="trips">
              <ErrorBoundary>
                <CanvasUI />
              </ErrorBoundary>
            </AlphaGateGuard>
          </ProtectedRoute>
        )}
      </Route>

      {/* Trip Detail - Canvas view is now default */}
      <Route path="/trips/:id">
        {(params) => (
          <ProtectedRoute>
            <AlphaGateGuard feature="trips">
              <ErrorBoundary>
                <TripPlannerCanvas />
              </ErrorBoundary>
            </AlphaGateGuard>
          </ProtectedRoute>
        )}
      </Route>

      {/* Legacy Trip Detail - moved to /legacy path */}
      <Route path="/trips/:id/legacy">
        {(params) => (
          <ProtectedRoute>
            <ErrorBoundary>
              <TripDetail />
            </ErrorBoundary>
          </ProtectedRoute>
        )}
      </Route>

      {/* Home page shows the Threads experience, kept as a separate component */}
      <Route path="/">
        {() => (
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        )}
      </Route>

      {/* Canvas Dashboard - Standalone page */}
      <Route path="/canvas-dashboard">
        {() => (
          <ProtectedRoute>
            <CanvasDashboard />
          </ProtectedRoute>
        )}
      </Route>

      {/* Clients - Canvas UI at dedicated route (supports deep links) */}
      <Route path="/clients">
        {() => (
          <ProtectedRoute>
            <AlphaGateGuard feature="clients">
              <ErrorBoundary>
                <CanvasUI />
              </ErrorBoundary>
            </AlphaGateGuard>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/clients/:id">
        {() => (
          <ProtectedRoute>
            <AlphaGateGuard feature="clients">
              <ErrorBoundary>
                <CanvasUI />
              </ErrorBoundary>
            </AlphaGateGuard>
          </ProtectedRoute>
        )}
      </Route>

      {/* Canvas Trips - Standalone page */}
      <Route path="/canvas-trips">
        {() => (
          <ProtectedRoute>
            <CanvasTrips />
          </ProtectedRoute>
        )}
      </Route>

      {/* Pipeline - use CanvasUI shell, not MainLayout */}
      <Route path="/pipeline">
        {() => (
          <ProtectedRoute>
            <AlphaGateGuard feature="pipeline">
              <ErrorBoundary>
                <CanvasUI />
              </ErrorBoundary>
            </AlphaGateGuard>
          </ProtectedRoute>
        )}
      </Route>

      {/* Admin routes - must be before /:rest* catch-all to avoid wouter nested routing issues */}
      {/* Admin User Editor (AR-327) — behind the `onboarding` parent flag
          (AR-171), off by default. With it off this renders NotFound, so a
          bookmarked member-record URL cannot reach a hidden surface. */}
      <Route path="/admin/users/:id">
        {() => (
          <ProtectedRoute allowedRoles={[...ADMIN_ROLE_SLUGS]}>
            <FeatureFlagGate flag="onboarding">
              <AdminShell>
                <ErrorBoundary>
                  <AdminUserRecordPage />
                </ErrorBoundary>
              </AdminShell>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/users">
        {() => (
          <ProtectedRoute allowedRoles={[...ADMIN_ROLE_SLUGS]}>
            <AdminShell>
              <ErrorBoundary>
                <AdminUsersPage />
              </ErrorBoundary>
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      {/* Member Activation builder (AR-328) — sibling of User Management.
          Behind the `onboarding` parent flag (AR-171): with it off these routes
          render NotFound, so a bookmarked URL cannot reach a hidden section. */}
      <Route path="/admin/activation/archive">
        {() => (
          <ProtectedRoute allowedRoles={[...ADMIN_ROLE_SLUGS]}>
            <FeatureFlagGate flag="onboarding">
              <AdminShell>
                <ErrorBoundary>
                  <MemberActivationArchivePage />
                </ErrorBoundary>
              </AdminShell>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/activation/kpis">
        {() => (
          <ProtectedRoute allowedRoles={[...ADMIN_ROLE_SLUGS]}>
            <FeatureFlagGate flag="onboarding">
              <AdminShell>
                <ErrorBoundary>
                  <MemberActivationKpisPage />
                </ErrorBoundary>
              </AdminShell>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/activation/modules">
        {() => (
          <ProtectedRoute allowedRoles={[...ADMIN_ROLE_SLUGS]}>
            <FeatureFlagGate flag="onboarding">
              <AdminShell>
                <ErrorBoundary>
                  <MemberActivationModulesPage />
                </ErrorBoundary>
              </AdminShell>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/skills">
        {() => (
          <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]} adminModuleKey="admin.developer-controls" adminModuleMinLevel="full">
            <AdminShell>
              <SkillsManagementPage />
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/orchestration">
        {() => (
          // Orchestration Rollout console is gated by SYSTEM_ADMIN + agenticAdminAdvanced flag
          isFeatureEnabled("agenticAdminAdvanced") ? (
            <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]} adminModuleKey="admin.developer-controls" adminModuleMinLevel="full">
              <AdminShell>
                <OrchestrationRolloutPage />
              </AdminShell>
            </ProtectedRoute>
          ) : (
            <NotFound />
          )
        )}
      </Route>
      <Route path="/admin/mcp-tools">
        {() => (
          <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]} adminModuleKey="admin.developer-controls" adminModuleMinLevel="full">
            <AdminShell>
              <MCPToolManagement />
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/audit">
        {() => (
          <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]} adminModuleKey="admin.audit-logs">
            <AdminShell>
              <AdminAuditPage />
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/feature-gates">
        {() => (
          <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]} adminModuleKey="admin.developer-controls" adminModuleMinLevel="full">
            <AdminShell>
              <AdminFeatureGatesPage />
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/cost-dashboard">
        {() => (
          <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]} adminModuleKey="admin.developer-controls">
            <AdminShell>
              <CostDashboardPage />
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/chat-export">
        {() => (
          <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]} adminModuleKey="admin.exports">
            <AdminShell>
              <ChatExportPage />
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      {/* AR-379: Content Management (posts synced from PRO), admins only. */}
      <Route path="/admin/content">
        {() => (
          <ProtectedRoute allowedRoles={[...ADMIN_ROLE_SLUGS]}>
            <AdminShell>
              <ErrorBoundary>
                <AdminContentPage />
              </ErrorBoundary>
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/prompts">
        {() => (
          <ProtectedRoute allowedRoles={[...ADMIN_ROLE_SLUGS]}>
            <AdminShell>
              <AdminPromptsPage />
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/product-updates">
        {() => (
          <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]} adminModuleKey="admin.developer-controls" adminModuleMinLevel="full">
            <AdminShell>
              <AdminProductUpdatesPage />
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/sync-error-log">
        {() => (
          <ProtectedRoute allowedRoles={[...ADMIN_ROLE_SLUGS]}>
            <FeatureFlagGate flag="invoicingMvp" deniedFallback={<AccessDenied message="Finances access is required to view the Sync Error Log. If you believe this is a mistake, contact your administrator." />}>
              <AdminShell>
                <SyncErrorLogPage />
              </AdminShell>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin">
        {() => (
          <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]}>
            <AdminShell>
              <AdminOverview />
            </AdminShell>
          </ProtectedRoute>
        )}
      </Route>
      
      {/* Team Management - gated by invoicingMvp (resolved per-user from backend) */}
      <Route path="/settings/team">
        {() => (
          /* The migration redirect runs first: the legacy role allowlist would
             otherwise send a non-member of it to "/" instead of to the Account
             center, whose own guard decides what they may see. */
          <ReplacedByAccountCenter to={ACCOUNT_PATHS.team}>
            <ProtectedRoute allowedRoles={["AGENCY_OWNER", "SYSTEM_ADMIN", "ADMIN"]}>
              <FeatureFlagGate flag="invoicingMvp">
                <MainLayout>
                  <TeamManagement />
                </MainLayout>
              </FeatureFlagGate>
            </ProtectedRoute>
          </ReplacedByAccountCenter>
        )}
      </Route>

      {/*
        AR-380 — the Account center, gated by the migration parent flag AND
        its own sub-flag (resolved per user from the backend), so it can ship
        without AR-381's data binding. Every page has its own address; the deeper paths are
        registered before /account so wouter does not match the module home
        first. ACCOUNT_APP_ROUTE_PATHS in lib/accountPaths.ts is the same list.
      */}
      {ACCOUNT_PAGE_IDS.map((id) => (
        <Route key={id} path={ACCOUNT_PATHS[id]}>
          {() => (
            <ProtectedRoute>
              <FeatureFlagGate flag={ACCOUNT_CENTER_FLAGS}>
                <AccountLayout>
                  <ErrorBoundary>
                    <AccountPageGuard page={id}>
                      {ACCOUNT_PAGE_COMPONENTS[id]}
                    </AccountPageGuard>
                  </ErrorBoundary>
                </AccountLayout>
              </FeatureFlagGate>
            </ProtectedRoute>
          )}
        </Route>
      ))}
      {/* v15 removed the overview, so the module home is an address that
          resolves to the first page rather than a page of its own. */}
      <Route path={ACCOUNT_HOME}>
        {() => (
          <ProtectedRoute>
            <FeatureFlagGate flag={ACCOUNT_CENTER_FLAGS}>
              <Redirect to={ACCOUNT_PATHS[ACCOUNT_HOME_PAGE]} />
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>

      {/*
        AR-390 — the Settings module, gated by the migration parent flag AND
        its own sub-flag (resolved per user from the backend), so it can ship
        without AR-391's data wiring. Every page has its own address, including
        the two D-ACCT-38 parks: Proposal Builder and Payments + CardVault are
        out of the column and the search but still render by link.
        SETTINGS_APP_ROUTE_PATHS in lib/settingsPaths.ts is the same list.
        The module home is NOT here — `/settings` keeps its legacy route below
        and hands over through ReplacedBySettingsModule.
      */}
      {SETTINGS_ALL_PAGE_IDS.map((id) => (
        <Route key={id} path={SETTINGS_PATHS[id]}>
          {() => (
            <ProtectedRoute>
              <FeatureFlagGate flag={SETTINGS_MODULE_FLAGS}>
                <SettingsLayout>
                  <ErrorBoundary>{SETTINGS_PAGE_COMPONENTS[id]}</ErrorBoundary>
                </SettingsLayout>
              </FeatureFlagGate>
            </ProtectedRoute>
          )}
        </Route>
      ))}
      {/* D-ACCT-38 — the Products page is retired. An old link lands on CRM
          Settings, which carries its Phase-1 way in, rather than on a hold
          card or a 404. */}
      {Object.entries(SETTINGS_RETIRED_PATHS).map(([from, to]) => (
        <Route key={from} path={from}>
          {() => (
            <ProtectedRoute>
              <FeatureFlagGate flag={SETTINGS_MODULE_FLAGS}>
                <Redirect to={to} />
              </FeatureFlagGate>
            </ProtectedRoute>
          )}
        </Route>
      ))}

      {/*
        Finances module — gated by invoicingMvp.
        These Route paths must stay synchronized with FINANCES_PATHS in
        client/src/lib/financesPaths.ts (see FINANCES_APP_ROUTE_PATHS).
      */}
      <Route path={FINANCES_PATHS.invoicesNew}>
        {() => (
          <ProtectedRoute>
            <FeatureFlagGate flag="invoicingMvp">
              <FinancesLayout>
                <ErrorBoundary>
                  <CreateInvoice />
                </ErrorBoundary>
              </FinancesLayout>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>
      <Route path={FINANCES_TRIP_EDIT_ROUTE}>
        {() => (
          <ProtectedRoute>
            <FeatureFlagGate flag="invoicingMvp">
              <FinancesLayout>
                <ErrorBoundary>
                  <EditTrip />
                </ErrorBoundary>
              </FinancesLayout>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>
      <Route path={FINANCES_INVOICES_EDIT_ROUTE}>
        {() => (
          <ProtectedRoute>
            <FeatureFlagGate flag="invoicingMvp">
              <FinancesLayout>
                <ErrorBoundary>
                  <EditInvoice />
                </ErrorBoundary>
              </FinancesLayout>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>
      <Route path={FINANCES_PATHS.invoices}>
        {() => (
          <ProtectedRoute>
            <FeatureFlagGate flag="invoicingMvp">
              <FinancesLayout>
                <ErrorBoundary>
                  <Invoicing />
                </ErrorBoundary>
              </FinancesLayout>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>

      <Route path={FINANCES_PATHS.statements}>
        {() => (
          <ProtectedRoute>
            <FeatureFlagGate flag="invoicingMvp">
              <FinancesLayout>
                <ErrorBoundary>
                  <Statements />
                </ErrorBoundary>
              </FinancesLayout>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>

      <Route path={FINANCES_PATHS.snapshots}>
        {() => (
          <ProtectedRoute>
            <FeatureFlagGate flag="invoicingMvp">
              <FinancesLayout>
                <ErrorBoundary>
                  <Reports />
                </ErrorBoundary>
              </FinancesLayout>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>

      <Route path={FINANCES_PATHS.unclaimed}>
        {() => (
          <ProtectedRoute>
            <FeatureFlagGate flag="invoicingMvp">
              <FeatureFlagGate flag="unclaimedCommission">
                <UnclaimedAccessGate>
                  <FinancesLayout>
                    <ErrorBoundary>
                      <UnclaimedCommissions />
                    </ErrorBoundary>
                  </FinancesLayout>
                </UnclaimedAccessGate>
              </FeatureFlagGate>
            </FeatureFlagGate>
          </ProtectedRoute>
        )}
      </Route>

      {/* Legacy finances URLs → canonical /finances/* paths */}
      <Route path="/invoicing/new"><Redirect to={FINANCES_PATHS.invoicesNew} /></Route>
      <Route path="/invoicing/:id/edit">
        {(params) => <Redirect to={FINANCES_PATHS.invoiceEdit(params.id)} />}
      </Route>
      <Route path="/invoicing"><Redirect to={FINANCES_PATHS.invoices} /></Route>
      <Route path="/statements"><Redirect to={FINANCES_PATHS.statements} /></Route>
      <Route path="/reports"><Redirect to={FINANCES_PATHS.snapshots} /></Route>
      <Route path="/finances/snapshots"><Redirect to={FINANCES_PATHS.snapshots} /></Route>
      <Route path="/unclaimed"><Redirect to={FINANCES_PATHS.unclaimed} /></Route>
      {/* AR-210 (TC-41.2): short Finances URLs rendered a blank page instead of the screen. */}
      <Route path="/finances/new"><Redirect to={FINANCES_PATHS.invoicesNew} /></Route>
      <Route path="/finances"><Redirect to={FINANCES_PATHS.invoices} /></Route>

      {/* Routes with MainLayout */}
      <Route path="/:rest*">
        {() => (
    <MainLayout>
      <Switch>
        {/* Public routes */}
        <Route path="/landing" component={Homepage} />
        <Route path="/waiting-list" component={WaitingList} />
        <Route path="/login" component={Login} />
        <Route path="/register" component={Register} />
        
        {/* Dashboard - Protected route */}
        <Route path="/dashboard">
          {() => (
            <ProtectedRoute>
              <AlphaGateGuard feature="dashboard">
                <Dashboard />
              </AlphaGateGuard>
            </ProtectedRoute>
          )}
        </Route>
        
        {/* Protected routes - require authentication */}
        
        
        {/* Client management pages */}
        <Route path="/clients-old">
          {() => (
            <ProtectedRoute>
              <ClientList />
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/clients-old/new">
          {() => (
            <ProtectedRoute>
              <ClientForm />
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/clients-old/:id/edit">
          {() => (
            <ProtectedRoute>
              <ClientForm />
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/clients-old/:id">
          {() => (
            <ProtectedRoute>
              <ClientDetail />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/proposals/:id">
          {() => (
            <ProtectedRoute>
              <AlphaGateGuard feature="proposals">
                <ProposalView />
              </AlphaGateGuard>
            </ProtectedRoute>
          )}
        </Route>
        
        {/* New navigation routes with placeholders */}
        <Route path="/ai-assistant">
          {() => (
            <ProtectedRoute>
              <AIAssistant />
            </ProtectedRoute>
          )}
        </Route>
        


        <Route path="/workspace">
          {() => (
            <ProtectedRoute>
              <Workspace />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/prompt-manager">
          {() => (
            <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]}>
              <PromptManager />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/site-settings">
          {() => (
            <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]}>
              <SiteSettings />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/user-preferences">
          {() => (
            <ProtectedRoute>
              <UserPreferences />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/booking-tools">
          {() => (
            <ProtectedRoute>
              {isFeatureEnabled("booking") ? (
                <BookingTools />
              ) : (
                <DeferredFeaturePlaceholder
                  featureName="Booking"
                  icon={<Plane size={48} />}
                  description="Booking workflows are paused for Phase 1. Enable the booking flag when you're ready to revisit this module."
                />
              )}
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/payment-authorizations">
          {() => (
            <ProtectedRoute>
              <PaymentAuthorizations />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/reporting">
          {() => (
            <ProtectedRoute>
              {isFeatureEnabled("analytics") ? (
                <Reporting />
              ) : (
                <DeferredFeaturePlaceholder
                  featureName="Analytics"
                  icon={<BarChart3 size={48} />}
                  description="Analytics dashboards are deferred. Enable the analytics flag once Phase 2 kicks off."
                />
              )}
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/website-manager">
          {() => (
            <ProtectedRoute>
              {isFeatureEnabled("digitalPresence") ? (
                <WebsiteManager />
              ) : (
                <DeferredFeaturePlaceholder
                  featureName="Digital Presence"
                  icon={<Globe size={48} />}
                  description="Digital Presence tooling is hidden while deferred. Toggle the digitalPresence flag to reactivate it."
                />
              )}
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/marketing">
          {() => (
            <ProtectedRoute>
              {isFeatureEnabled("digitalPresence") ? (
                <Marketing />
              ) : (
                <DeferredFeaturePlaceholder
                  featureName="Digital Presence"
                  icon={<Globe size={48} />}
                  description="Marketing tools are deferred with the Digital Presence module. Enable the digitalPresence flag when it's time to ship."
                />
              )}
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/knowledge">
          {() => (
            <ProtectedRoute>
              <Knowledge />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/knowledge-graph-demo">
          {() => (
            <ProtectedRoute>
              <KnowledgeGraphDemo />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/training">
          {() => (
            <ProtectedRoute>
              <Training />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/sales-pipeline">
          {() => (
            <ProtectedRoute>
              <AlphaGateGuard feature="pipeline">
                <SalesPipeline />
              </AlphaGateGuard>
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/sales-plans">
          {() => (
            <ProtectedRoute>
              <SalesPlanPage />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/product-updates">
          {() => (
            <ProtectedRoute>
              <ProductUpdates />
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/settings">
          {() => (
            <ProtectedRoute>
              {/* AR-390 — the module home. v15 has no overview page, so
                  `/settings` is an address that resolves to the first page
                  (Color Theme) rather than a page of its own. */}
              <ReplacedBySettingsModule to={SETTINGS_PATHS[SETTINGS_HOME_PAGE]}>
                <SettingsPage />
              </ReplacedBySettingsModule>
            </ProtectedRoute>
          )}
        </Route>
        
        <Route path="/profile">
          {() => (
            <ProtectedRoute>
              <ReplacedByAccountCenter to={ACCOUNT_HOME}>
                <UserProfile />
              </ReplacedByAccountCenter>
            </ProtectedRoute>
          )}
        </Route>
        
        {/* Admin routes moved to outer Switch to fix wouter nested routing issue */}
        
        <Route component={NotFound} />
      </Switch>
    </MainLayout>
        )}
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ImpersonationProvider>
          {/* The color theme, the display mode and the text size are platform-wide
              (D-ACCT-24: the text size scales the WHOLE app), so ThemeProvider
              applies them above the router. Since AR-393 it is the ONE writer:
              the three are the member record's, read from /api/user/preferences,
              and AR-391 retired the localStorage-only AppearanceProvider that
              used to stamp the same attributes beside it. */}
          <ThemeProvider>
            <Auth0ConfigBanner />
            <Router>
              <UnsavedChangesProvider>
                <BrowserHistoryGuard />
                <RouteAnnouncer />
                <AppRoutes />
              </UnsavedChangesProvider>
            </Router>
            <ProductUpdatePopup />
            <Toaster />
            <UseToastToaster />
          </ThemeProvider>
        </ImpersonationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
